import { UserRole } from '@/constants/roles.constant'

export type AppEnvironment = 'development' | 'production' | 'staging'

export interface AppConfig {
  apiBaseUrl: string
  apiPrefix: string
  unAuthenticatedEntryPath: string
  authenticatedEntryPath: string
  entryPath: Record<UserRole, string>
  appVersion: string
  environment: AppEnvironment
}

const sanitizeApiBaseUrl = (baseUrl: string): string => {
  return baseUrl.replace(/\/$/, '')
}

const normalizeEnvironment = (value?: string): AppEnvironment | null => {
  if (!value) return null

  const normalized = value.trim().toLowerCase()

  if (normalized === 'production' || normalized === 'prod') return 'production'
  if (normalized === 'staging' || normalized === 'stage' || normalized === 'preview') return 'staging'
  if (normalized === 'development' || normalized === 'dev' || normalized === 'local') return 'development'

  return null
}

const resolveAppEnvironment = (): AppEnvironment => {
  const explicitEnv =
    normalizeEnvironment(process.env.NEXT_PUBLIC_APP_ENV) ||
    normalizeEnvironment(process.env.APP_ENV)

  if (explicitEnv) {
    return explicitEnv
  }

  throw new Error(
    '[AppConfig] Missing APP_ENV/NEXT_PUBLIC_APP_ENV. Set one of: development | staging | production.'
  )
}

const normalizeClientApiBaseUrl = (baseUrl: string): string => {
  const sanitizedBaseUrl = sanitizeApiBaseUrl(baseUrl)

  if (typeof window === 'undefined') {
    return sanitizedBaseUrl
  }

  try {
    const url = new URL(sanitizedBaseUrl)

    // On phones/tablets, localhost points to the device itself.
    // Reuse the current frontend host so API requests hit the dev machine.
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      url.hostname = window.location.hostname
      return url.toString().replace(/\/$/, '')
    }

    return sanitizedBaseUrl
  } catch {
    return sanitizedBaseUrl
  }
}

const getApiBaseUrl = (environment: AppEnvironment): string => {
  void environment
  const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL?.trim() || null
  if (!apiBaseUrl) {
    console.warn(
      '[AppConfig] Missing NEXT_PUBLIC_API_BASE_URL. Falling back to relative /api. Configure this env var for correct API routing.'
    )
    return '/api'
  }
  return normalizeClientApiBaseUrl(apiBaseUrl)
}

const environment = resolveAppEnvironment()

export const appConfig: AppConfig = {
  apiBaseUrl: getApiBaseUrl(environment),
  apiPrefix: '/api',
  unAuthenticatedEntryPath: '/auth/login',
  authenticatedEntryPath: '/',
  entryPath: {
    [UserRole.TEACHER]: '/dashboard/teacher/today',
    [UserRole.PARENT]: '/dashboard/parent/home',
    [UserRole.SCHOOL_ADMIN]: '/dashboard/admin/dashboard',
    [UserRole.ACCOUNTANT]: '/dashboard/admin/dashboard',
    [UserRole.SUPER_ADMIN]: '/dashboard/super-admin/schools',
  },
  appVersion: '1.0.0',
  environment,
}
